import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, CalendarCheck, TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { KpiData } from '../../hooks/useStatistics';

interface Props {
  kpis: KpiData;
  loading: boolean;
}

function calcChange(current: number, previous: number): { value: number; direction: 'up' | 'down' | 'stable' } {
  if (previous === 0 && current === 0) return { value: 0, direction: 'stable' };
  if (previous === 0) return { value: 100, direction: 'up' };
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) return { value: 0, direction: 'stable' };
  return { value: Math.abs(Math.round(pct)), direction: pct > 0 ? 'up' : 'down' };
}

const StatisticsKpiCards: React.FC<Props> = ({ kpis, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-10 w-10 bg-gray-200 rounded-lg" />
            </div>
            <div className="h-9 bg-gray-200 rounded w-20 mb-3" />
            <div className="h-5 bg-gray-100 rounded w-28" />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Conversations',
      value: kpis.totalConversations.toLocaleString('fr-FR'),
      icon: MessageSquare,
      change: calcChange(kpis.totalConversations, kpis.previousConversations),
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      label: 'RDV obtenus',
      value: kpis.totalRdvs.toLocaleString('fr-FR'),
      icon: CalendarCheck,
      change: calcChange(kpis.totalRdvs, kpis.previousRdvs),
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-500',
    },
    {
      label: 'Taux de conversion',
      value: `${kpis.conversionRate.toFixed(1)}%`,
      icon: TrendingUp,
      change: calcChange(kpis.conversionRate, kpis.previousConversionRate),
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {cards.map((card, index) => {
        const Icon = card.icon;
        const { value: changePct, direction } = card.change;

        return (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-500">{card.label}</p>
              <div className={`${card.iconBg} ${card.iconColor} p-2.5 rounded-lg`}>
                <Icon className="h-5 w-5" />
              </div>
            </div>

            <p className="text-3xl font-bold text-gray-900 mb-3">{card.value}</p>

            <div className="flex items-center space-x-1.5">
              {direction === 'up' && (
                <span className="inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                  <ArrowUp className="h-3 w-3 mr-0.5" />
                  +{changePct}%
                </span>
              )}
              {direction === 'down' && (
                <span className="inline-flex items-center text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                  <ArrowDown className="h-3 w-3 mr-0.5" />
                  -{changePct}%
                </span>
              )}
              {direction === 'stable' && (
                <span className="inline-flex items-center text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  <Minus className="h-3 w-3 mr-0.5" />
                  0%
                </span>
              )}
              <span className="text-xs text-gray-400">vs periode prec.</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default StatisticsKpiCards;
